import React from 'react';
import { CheckCircle2, AlertTriangle, XCircle, Info, X } from 'lucide-react';
import { cn } from '../utils/cn';
import { TONE_CLASSES, type Tone } from '../utils/statusTone';

export interface ILMSAlertProps {
  tone?: Tone;
  title?: string;
  children?: React.ReactNode;
  onDismiss?: () => void;
  action?: React.ReactNode;
  className?: string;
}

const TONE_ICON: Record<Tone, React.ComponentType<{ className?: string }>> = {
  success: CheckCircle2,
  warning: AlertTriangle,
  critical: XCircle,
  info: Info,
  neutral: Info,
};

export function ILMSAlert({ tone = 'info', title, children, onDismiss, action, className }: ILMSAlertProps) {
  const classes = TONE_CLASSES[tone];
  const Icon = TONE_ICON[tone];
  return (
    <div role="alert" className={cn('flex gap-3 rounded-md border p-3 sm:p-4', classes.bg, classes.border, className)}>
      <Icon className={cn('h-5 w-5 shrink-0 mt-0.5', classes.text)} aria-hidden="true" />
      <div className="min-w-0 flex-1">
        {title && <p className={cn('font-semibold text-sm', classes.text)}>{title}</p>}
        {children && <div className="text-secondary text-ink-700 mt-0.5">{children}</div>}
        {action && <div className="mt-2">{action}</div>}
      </div>
      {onDismiss && (
        <button
          onClick={onDismiss}
          aria-label="Dismiss"
          className={cn('shrink-0 rounded p-1 hover:bg-black/5', classes.text)}
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
